/**
 * TRIAL SERVICE - DEMO-ONLY UI STUB LAYER
 *
 * This service provides a client-side demo implementation of trial and premium features.
 * All functions are placeholders that will be replaced with actual backend API calls.
 *
 * DO NOT call setPremiumStatus() from user-facing flows - this is for testing only.
 * Premium status should be managed by the authService and backend.
 *
 * When backend integration is complete, this file will be replaced with calls to authService.
 */

import { TrialInfo, TrialStatus } from '../types';
import { DAILY_MANUAL_KEY, DAILY_RECIPE_KEY, DAILY_SCAN_KEY, getItem, PREMIUM_KEY, removeItem, setItem, TRIAL_KEY } from './storageService';
import {
  TRIAL_DURATION_MS,
  TRIAL_DURATION_DAYS,
  FREE_DAILY_SCAN_LIMIT,
  FREE_DAILY_RECIPE_LIMIT,
  FREE_DAILY_MANUAL_LIMIT,
  UNLIMITED,
} from '../config/businessRules';


/**
 * DEMO-ONLY: Start the trial period if not already started.
 * Called when user first accesses pantry scanning feature.
 *
 * TODO: Replace with backend API call when user first accesses premium features
 */
export const startTrialIfNeeded = (): void => {
  if (!getItem(TRIAL_KEY)) {
    setItem(TRIAL_KEY, new Date().toISOString());
  }
};

/**
 * DEMO-ONLY: Get the current trial status for pantry scanning.
 * @returns 'active' | 'expired' | 'premium'
 *
 * TODO: Replace with authService.getSubscriptionTier() call
 */
export const getTrialStatus = (): TrialStatus => {
  // Check premium first (overrides trial)
  if (isPremium()) {
    return 'premium';
  }

  const trialStart = getItem(TRIAL_KEY);

  // Trial not started yet - will be 'active' once they scan
  if (!trialStart) {
    return 'active';
  }

  const elapsed = Date.now() - new Date(trialStart).getTime();
  return elapsed < TRIAL_DURATION_MS ? 'active' : 'expired';
};

/**
 * DEMO-ONLY: Get the number of days remaining in the trial.
 * @returns Number of days remaining (0-7), 0 if expired, -1 if premium
 *
 * TODO: Replace with authService.getSubscriptionTier() call
 */
export const getTrialDaysRemaining = (): number => {
  if (isPremium()) {
    return UNLIMITED; // Premium users have unlimited access
  }

  const trialStart = getItem(TRIAL_KEY);

  if (!trialStart) {
    return TRIAL_DURATION_DAYS; // Full trial available
  }

  const elapsed = Date.now() - new Date(trialStart).getTime();
  const remaining = TRIAL_DURATION_MS - elapsed;

  if (remaining <= 0) {
    return 0;
  }

  // Convert ms to days, round up
  return Math.ceil(remaining / (24 * 60 * 60 * 1000));
};

/**
 * DEMO-ONLY: Check if user is premium (placeholder for future backend integration).
 * Currently checks localStorage; will be replaced with API call.
 *
 * TODO: Replace with authService.getSubscriptionTier() call
 */
export const isPremium = (): boolean => {
  return getItem(PREMIUM_KEY) === 'true';
};

/**
 * DEMO-ONLY: Get complete trial information for UI display.
 *
 * TODO: Replace with authService.getSubscriptionTier() call
 */
export const getTrialInfo = (): TrialInfo => {
  return {
    status: getTrialStatus(),
    startDate: getItem(TRIAL_KEY),
    daysRemaining: getTrialDaysRemaining()
  };
};

/**
 * DEMO-ONLY: Reset trial (for testing purposes only).
 * Remove in production or guard behind dev flag.
 *
 * TODO: Remove in production - only for development testing
 */
export const resetTrial = (): void => {
  removeItem(TRIAL_KEY);
  removeItem(PREMIUM_KEY);
  removeItem(DAILY_MANUAL_KEY);
  removeItem(DAILY_SCAN_KEY);
  removeItem(DAILY_RECIPE_KEY);
};

/**
 * DEMO-ONLY: Set premium status (for testing purposes only).
 * In production, this would be set by backend after payment.
 *
 * DO NOT CALL FROM USER-FACING FLOWS - This is for testing only!
 * Premium status should be managed by authService and backend.
 *
 * TODO: Remove in production - premium status managed by backend only
 */
export const setPremiumStatus = (isPremium: boolean): void => {
  if (isPremium) {
    setItem(PREMIUM_KEY, 'true');
  } else {
    removeItem(PREMIUM_KEY);
  }
};
/**
 * DEMO-ONLY: Get the current daily manual ingredient count for free users.
 * Resets daily based on date.
 *
 * TODO: Replace with backend API call for usage tracking
 */
export const getDailyManualCount = (): number => {
  const stored = getItem(DAILY_MANUAL_KEY);
  if (!stored) return 0;

  const data = JSON.parse(stored);
  const today = new Date().toDateString();

  if (data.date !== today) {
    // Reset for new day
    removeItem(DAILY_MANUAL_KEY);
    return 0;
  }

  return data.count || 0;
};

/**
 * DEMO-ONLY: Increment the daily manual ingredient count.
 * Only increments if under the limit.
 * @returns true if increment was allowed, false if limit reached
 *
 * TODO: Replace with backend API call for usage tracking
 */
export const incrementDailyManualCount = (): boolean => {
  const current = getDailyManualCount();
  if (current >= FREE_DAILY_MANUAL_LIMIT) return false;

  const today = new Date().toDateString();
  setItem(DAILY_MANUAL_KEY, JSON.stringify({ date: today, count: current + 1 }));
  return true;
};

/**
 * DEMO-ONLY: Check if manual ingredient addition is allowed for free users.
 *
 * TODO: Replace with authService.getSubscriptionTier() call
 */
export const canAddManualIngredient = (): boolean => {
  return isPremium() || getDailyManualCount() < FREE_DAILY_MANUAL_LIMIT;
};

/**
 * DEMO-ONLY: Get the current daily scan count for free users.
 * Resets daily based on date.
 *
 * TODO: Replace with backend API call for usage tracking
 */
export const getDailyScanCount = (): number => {
  const stored = getItem(DAILY_SCAN_KEY);
  if (!stored) return 0;

  const data = JSON.parse(stored);
  const today = new Date().toDateString();

  if (data.date !== today) {
    // Reset for new day
    removeItem(DAILY_SCAN_KEY);
    return 0;
  }

  return data.count || 0;
};

/**
 * DEMO-ONLY: Increment the daily scan count.
 * Only increments if under the limit.
 * @returns true if increment was allowed, false if limit reached
 *
 * TODO: Replace with backend API call for usage tracking
 */
export const incrementDailyScanCount = (): boolean => {
  const current = getDailyScanCount();
  if (current >= FREE_DAILY_SCAN_LIMIT) return false;

  const today = new Date().toDateString();
  setItem(DAILY_SCAN_KEY, JSON.stringify({ date: today, count: current + 1 }));
  return true;
};

/**
 * DEMO-ONLY: Check if pantry scanning is allowed for free users.
 *
 * TODO: Replace with authService.getSubscriptionTier() call
 */
export const canScanPantry = (): boolean => {
  return isPremium() || (getTrialStatus() === 'active' && getDailyScanCount() < FREE_DAILY_SCAN_LIMIT);
};

/**
 * DEMO-ONLY: Get the current daily recipe generation count for free users.
 * Resets daily based on date.
 *
 * TODO: Replace with backend API call for usage tracking
 */
export const getDailyRecipeCount = (): number => {
  const stored = getItem(DAILY_RECIPE_KEY);
  if (!stored) return 0;

  const data = JSON.parse(stored);
  const today = new Date().toDateString();

  if (data.date !== today) {
    // Reset for new day
    removeItem(DAILY_RECIPE_KEY);
    return 0;
  }

  return data.count || 0;
};

/**
 * DEMO-ONLY: Increment the daily recipe generation count.
 * Only increments if under the limit.
 * @returns true if increment was allowed, false if limit reached
 *
 * TODO: Replace with backend API call for usage tracking
 */
export const incrementDailyRecipeCount = (): boolean => {
  const current = getDailyRecipeCount();
  if (current >= FREE_DAILY_RECIPE_LIMIT) return false;

  const today = new Date().toDateString();
  setItem(DAILY_RECIPE_KEY, JSON.stringify({ date: today, count: current + 1 }));
  return true;
};

/**
 * DEMO-ONLY: Check if recipe generation is allowed for free users.
 *
 * TODO: Replace with authService.getSubscriptionTier() call
 */
export const canGenerateRecipe = (): boolean => {
  return isPremium() || getDailyRecipeCount() < FREE_DAILY_RECIPE_LIMIT;
};

/**
 * DEMO-ONLY: Get remaining recipe generations for today (free tier).
 * @returns Number of recipes remaining (0-5), or -1 for premium users
 *
 * TODO: Replace with authService.getSubscriptionTier() call
 */
export const getRemainingRecipes = (): number => {
  if (isPremium()) {
    return UNLIMITED; // Unlimited for premium
  }
  return Math.max(0, FREE_DAILY_RECIPE_LIMIT - getDailyRecipeCount());
};
